import { Truck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useChannelStore } from '@/store/channel'

interface FreeShippingBannerProps {
  /** 'list' = show every configured zone (Home/Product); 'cart' = order-aware. */
  variant?: 'list' | 'cart'
  /** Order subtotal with tax, in cents. Required for variant='cart'. */
  subTotalWithTax?: number
  /** Resolved shipping zone name for the order, e.g. activeOrder.shippingAddress?.zone?.name. */
  zoneName?: string | null
  className?: string
}

const formatEuros = (cents: number) =>
  new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(cents / 100)

export default function FreeShippingBanner({
  variant = 'list',
  subTotalWithTax,
  zoneName,
  className = '',
}: FreeShippingBannerProps) {
  const { t } = useTranslation()
  const freeShippingRates = useChannelStore((s) => s.freeShippingRates)

  if (freeShippingRates.length === 0) return null

  if (variant === 'cart' && zoneName) {
    const rate = freeShippingRates.find((r) => r.zoneName === zoneName)
    if (!rate) return null

    const qualifies = (subTotalWithTax ?? 0) >= rate.minAmountEUR
    return (
      <div className={`text-xs flex items-center gap-2 ${className}`}>
        <Truck className="w-4 h-4 text-secondary shrink-0" />
        <span>
          {qualifies
            ? t('freeShipping.achieved')
            : t('freeShipping.remaining', {
                amount: formatEuros(rate.minAmountEUR - (subTotalWithTax ?? 0)),
                zone: zoneName,
              })}
        </span>
      </div>
    )
  }

  return (
    <div className={`text-xs ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <Truck className="w-4 h-4 text-secondary shrink-0" />
        <strong>{t('freeShipping.title')}</strong>
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-foreground/70">
        {freeShippingRates.map((rate) => (
          <li key={rate.zoneName}>
            {t('freeShipping.zoneLine', { zone: rate.zoneName, amount: formatEuros(rate.minAmountEUR) })}
          </li>
        ))}
      </ul>
    </div>
  )
}
